import { useMemo, useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

// Shared SVG path for star icons
const STAR_PATH = "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"

const formatPrice = (price, currency = 'EUR', locale = 'de-DE') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(price)
}

const getDiscountPercent = (original, sale) => {
  if (!original || original <= sale) return null
  return Math.round((1 - sale / original) * 100)
}

function StarRating({ rating, size = 'sm' }) {
  const gradientId = useId()
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'

  return (
    <div className="flex items-center" role="img" aria-label={`${rating} out of 5 stars`}>
      {[...Array(fullStars)].map((_, i) => (
        <svg key={`full-${i}`} className={`${sizeClass} text-yellow-400`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d={STAR_PATH} />
        </svg>
      ))}
      {hasHalfStar && (
        <svg className={`${sizeClass} text-yellow-400`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <defs>
            <linearGradient id={gradientId}>
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="#E5E7EB" />
            </linearGradient>
          </defs>
          <path fill={`url(#${gradientId})`} d={STAR_PATH} />
        </svg>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <svg key={`empty-${i}`} className={`${sizeClass} text-gray-300`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d={STAR_PATH} />
        </svg>
      ))}
    </div>
  )
}

StarRating.propTypes = {
  rating: PropTypes.number.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
}

const PLACEHOLDER_IMAGE = '/placeholder-product.svg'

function ProductCard({
  product,
  selectable = false,
  selected = false,
  onSelect,
  onClick,
  showAnalytics = true,
}) {
  const { t, i18n } = useTranslation('ecommerce')
  const locale = i18n.language || 'de-DE'
  const {
    product_id,
    title,
    price,
    currency = 'EUR',
    original_price,
    image_url,
    is_featured,
    inventory_status,
    // Rating data
    aggregate_rating,
    review_count,
    // Analytics
    click_count,
    story_count,
    last_used_in_story_at,
    // Inventory
    stock_count,
    video_url,
  } = product

  const discountPercent = useMemo(
    () => getDiscountPercent(original_price, price),
    [original_price, price]
  )

  const isOutOfStock = inventory_status === 'out_of_stock'
  const isLowStock = stock_count !== null && stock_count !== undefined && stock_count > 0 && stock_count <= 10

  const handleCheckboxChange = useCallback((e) => {
    e.stopPropagation()
    onSelect?.(product_id, e.target.checked)
  }, [onSelect, product_id])

  const handleCardClick = useCallback(() => {
    if (selectable) {
      onSelect?.(product_id, !selected)
    } else {
      onClick?.(product)
    }
  }, [selectable, onSelect, onClick, product_id, selected, product])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleCardClick()
    }
  }, [handleCardClick])

  const handleImageError = useCallback((e) => {
    e.target.src = PLACEHOLDER_IMAGE
  }, [])

  const lastStoryRelative = useMemo(() => {
    if (!last_used_in_story_at) return null
    const date = new Date(last_used_in_story_at)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return t('products.analytics.relativeTime.today', 'Today')
    if (diffDays === 1) return t('products.analytics.relativeTime.yesterday', 'Yesterday')
    if (diffDays < 7) return t('products.analytics.relativeTime.daysAgo', '{{count}} days ago', { count: diffDays })
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return t('products.analytics.relativeTime.weeksAgo', '{{count}} week ago', { count: weeks })
    }
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  }, [last_used_in_story_at, locale, t])

  return (
    <div
      className={`
        relative bg-white rounded-lg border shadow-sm overflow-hidden
        transition-all duration-200 cursor-pointer
        ${selected ? 'ring-2 ring-primary-500 border-primary-500' : 'border-gray-200 hover:border-gray-300'}
        ${isOutOfStock ? 'opacity-60' : ''}
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
      `}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-pressed={selectable ? selected : undefined}
      aria-label={title}
    >
      {/* Checkbox */}
      {selectable && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={selected}
            onChange={handleCheckboxChange}
            aria-label={t('products.selectProduct', 'Select {{title}}', { title })}
            className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      )}

      {/* Badges */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        {is_featured && (
          <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
            {t('products.badges.featured', 'Featured')}
          </span>
        )}
        {discountPercent && (
          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
            -{discountPercent}%
          </span>
        )}
        {isLowStock && !isOutOfStock && (
          <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
            {t('products.badges.lowStock', 'Low Stock')} ({stock_count})
          </span>
        )}
        {isOutOfStock && (
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">
            {t('products.badges.outOfStock', 'Out of Stock')}
          </span>
        )}
        {video_url && (
          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded flex items-center gap-1">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {t('products.badges.shopVideo', 'Shop-Video')}
          </span>
        )}
      </div>

      {/* Image */}
      <div className="aspect-square bg-gray-100">
        <img
          src={image_url || PLACEHOLDER_IMAGE}
          alt={title}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
          {title}
        </h3>

        {/* Rating */}
        {aggregate_rating && (
          <div className="flex items-center gap-1 mb-1">
            <StarRating rating={aggregate_rating} size="sm" />
            <span className="text-xs text-gray-600">
              {aggregate_rating.toFixed(1)}
              {review_count && (
                <span className="text-gray-400"> ({review_count.toLocaleString(locale)})</span>
              )}
            </span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-gray-900">
            {formatPrice(price, currency, locale)}
          </span>
          {original_price && original_price > price && (
            <span className="text-sm text-gray-500 line-through">
              {formatPrice(original_price, currency, locale)}
            </span>
          )}
        </div>

        {/* Analytics Stats - Always shown when showAnalytics is true */}
        {showAnalytics && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <div className="flex items-center gap-1" title={t('products.analytics.clicks', 'Clicks from Bio Page')}>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <span>{(click_count || 0).toLocaleString(locale)}</span>
            </div>
            <div className="flex items-center gap-1" title={t('products.analytics.stories', 'Stories Created')}>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>{story_count || 0}</span>
            </div>
            <div className="flex items-center gap-1" title={t('products.analytics.lastStory', 'Last Posted')}>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{lastStoryRelative || t('products.analytics.never', 'Never')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    product_id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    currency: PropTypes.string,
    original_price: PropTypes.number,
    image_url: PropTypes.string,
    is_featured: PropTypes.bool,
    inventory_status: PropTypes.string,
    aggregate_rating: PropTypes.number,
    review_count: PropTypes.number,
    click_count: PropTypes.number,
    story_count: PropTypes.number,
    last_used_in_story_at: PropTypes.string,
    stock_count: PropTypes.number,
    video_url: PropTypes.string,
  }).isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  onClick: PropTypes.func,
  showAnalytics: PropTypes.bool,
}

export default ProductCard
